import { useCallback, useEffect, useRef, useState } from 'react';

interface IUseCountdownTimerParams {
  durationSeconds: number;
  autoStart?: boolean;
  onTick?: (timeLeft: number) => void;
  onComplete?: () => void;
}

export const useCountdownTimer = ({
  durationSeconds,
  autoStart = false,
  onTick,
  onComplete,
}: IUseCountdownTimerParams) => {
  const [timeLeftSeconds, setTimeLeftSeconds] = useState(durationSeconds);
  const [isRunning, setIsRunning] = useState(autoStart);

  const intervalRef = useRef<NodeJS.Timer | null>(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    setIsRunning(true);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
    clearTimer();
  }, [clearTimer]);

  useEffect(() => {
    setTimeLeftSeconds(durationSeconds);
  }, [durationSeconds]);

  useEffect(() => {
    if (!isRunning) return;

    intervalRef.current = setInterval(() => {
      setTimeLeftSeconds((prev) => {
        if (prev <= 1) {
          clearTimer();
          onTick?.(0);
          onComplete?.();
          return 0;
        }

        const newTime = prev - 1;
        onTick?.(newTime);
        return newTime;
      });
    }, 1000);

    return () => {
      clearTimer();
    };
  }, [isRunning, clearTimer, onTick, onComplete]);

  return {
    timeLeftSeconds,
    isRunning,
    start,
    stop,
  };
};
